/**
 * Contract between the main-process performance monitor
 * (`@main/utils/performance-monitor`) and the debug overlay
 * (`@renderer/components/PerformanceMonitor`).
 *
 * Snapshots stream over the broadcast broker — zero registered consumers
 * means zero IPC — never over GlobalState. The only GlobalState involvement
 * is the low-frequency `debug.performanceOverlay` on/off flag.
 */
export const PERFORMANCE_MONITOR_TOPIC = 'debug.performance'

/** Low-frequency GlobalState flag that gates both sampling (main) and the overlay UI (renderer). */
export const PERFORMANCE_OVERLAY_FLAG_KEY = 'debug.performanceOverlay'

export interface ProcessPerformanceSample {
  pid: number
  /** Short display label: 'main', 'gpu', renderer entry name ('overlay', 'chat', …), or utility service name. */
  label: string
  /** Electron process type ('Browser', 'Tab', 'GPU', 'Utility', …). */
  type: string
  /** CPU usage since the previous sample, in percent of one core. */
  cpuPercent: number
  /** Working set currently pinned to physical RAM, in MB. */
  memoryMB: number
  /** Idle wakeups per second (macOS/Linux; always 0 on Windows). */
  idleWakeupsPerSecond: number
  /**
   * Rough per-process approximation of Activity Monitor's "Energy Impact":
   * CPU share plus a weighted idle-wakeup cost. Comparable across processes
   * and over time, not calibrated to Apple's exact units.
   */
  energyImpact: number
  /**
   * Measured average power over the sampling interval, from the kernel's
   * per-task energy counter (`proc_pid_rusage` → `ri_energy_nj`). Apple
   * Silicon only; absent on Intel, Windows, and the first sample per pid.
   */
  powerMilliwatts?: number
}

export type IpcFanoutRateUnit = 'second' | 'minute'

export interface IpcFanoutSourceRate {
  /** Key such as 'global.state:some-key' or 'global.events:some-event'. */
  source: string
  rate: number
}

/** Fan-out rate of GlobalState/GlobalStore/GlobalEvents frames sent from main to every renderer. */
export interface IpcFanoutSample {
  /** Total frames per second fanned out over the sampling interval. */
  framesPerSecond: number
  /** Unit used by every source rate in this sample. */
  sourceRateUnit: IpcFanoutRateUnit
  /** Every source observed during this monitor session, descending by current rate. */
  sourceRates: Array<IpcFanoutSourceRate>
}

/** Rolling timings for the meeting AX poll loop (participants / self-mute tree walks). */
export interface AxPollSample {
  /** Display name of the active AX service (e.g. 'Google Meet'). */
  service: string
  lastMs: number
  avgMs: number
  maxMs: number
  pollsPerMinute: number
}

export interface PerformanceSnapshot {
  at: number
  totals: {
    cpuPercent: number
    memoryMB: number
    idleWakeupsPerSecond: number
    energyImpact: number
    /** Sum of per-process measured power; absent when no process reported it. */
    powerMilliwatts?: number
  }
  processes: Array<ProcessPerformanceSample>
  /** Present while the monitor runs; measures main→renderer IPC fan-out pressure. */
  ipcFanout?: IpcFanoutSample
  /** Present while an AX meeting service is actively polling. */
  axPolls?: Array<AxPollSample>
}
